import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Snackbar, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  ENTERPRISE_TOAST_COMPACT,
  ENTERPRISE_TOAST_SHELL,
} from "./tokens";
import EnterpriseMessageDetailDialog from "./EnterpriseMessageDetailDialog";

const VARIANT_ICONS = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberOutlinedIcon,
  info: InfoOutlinedIcon,
  infoToast: CloudDoneOutlinedIcon,
};

function resolveToastTokens(variant) {
  return ENTERPRISE_TOAST_COMPACT[variant] || ENTERPRISE_TOAST_COMPACT.info;
}

/**
 * Bottom-center compact toast — tap truncated message for centered full-text dialog.
 * @see DesignTest/DESIGN.md — Messages, Alerts & Feedback
 */
export default function EnterpriseMessageToast({
  open,
  variant = "info",
  title,
  description,
  message,
  /** Full copy for detail dialog; defaults to message / description. */
  detail,
  actionLabel,
  onAction,
  onClose,
  autoHideDuration = 6000,
}) {
  const tokens = resolveToastTokens(variant);
  const Icon = VARIANT_ICONS[variant] || InfoOutlinedIcon;
  const shell = ENTERPRISE_TOAST_SHELL;
  const previewLine = message || title || description || "";
  const fullBody = detail || description || message || title || "";

  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setDetailOpen(false);
      return undefined;
    }
    const measure = () => {
      const el = textRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const id = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", measure);
    };
  }, [open, previewLine]);

  const openDetail = () => {
    if (!fullBody) return;
    setDetailOpen(true);
  };

  const handleToastClose = (event, reason) => {
    if (detailOpen) return;
    onClose?.(event, reason);
  };

  const canExpand = Boolean(fullBody);

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={detailOpen ? null : autoHideDuration}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 1, sm: 0 } }}
      >
        <Box
          role="status"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: shell.gap,
            px: shell.px,
            py: shell.py,
            minHeight: shell.minHeight,
            boxSizing: "border-box",
            borderRadius: "9999px",
            backgroundColor: tokens.surface,
            color: tokens.onSurface,
            width: { xs: shell.widthXs, sm: shell.widthSm },
            maxWidth: shell.maxWidth,
            boxShadow: "0px 4px 8px 3px rgba(28, 27, 31, 0.12)",
          }}
        >
          <Icon sx={{ fontSize: shell.iconSize, color: tokens.icon, flexShrink: 0 }} />
          <Typography
            ref={textRef}
            component={canExpand ? "button" : "p"}
            type={canExpand ? "button" : undefined}
            onClick={canExpand ? openDetail : undefined}
            noWrap
            title={isTruncated ? previewLine : undefined}
            aria-label={
              canExpand
                ? isTruncated
                  ? "Message truncated. Open full message."
                  : "Open full message"
                : undefined
            }
            sx={{
              flex: 1,
              minWidth: 0,
              border: "none",
              background: "none",
              textAlign: "left",
              p: 0,
              cursor: canExpand ? "pointer" : "default",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
              fontSize: shell.messageFontSize,
              lineHeight: 1.35,
              color: "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textDecoration: isTruncated ? "underline dotted" : "none",
              textUnderlineOffset: "3px",
            }}
          >
            {previewLine}
          </Typography>
          {actionLabel ? (
            <Typography
              component="button"
              type="button"
              onClick={() => {
                onAction?.();
                onClose?.({}, "actionClick");
              }}
              sx={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                fontSize: shell.actionFontSize,
                color: tokens.accent,
                flexShrink: 0,
                px: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {actionLabel}
            </Typography>
          ) : null}
          <IconButton
            size="small"
            aria-label="Close"
            onClick={onClose}
            sx={{
              color: tokens.muted,
              ml: -0.5,
              p: 0.5,
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: shell.closeSize },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Snackbar>

      <EnterpriseMessageDetailDialog
        open={detailOpen}
        variant={variant}
        title={title || (isTruncated ? undefined : previewLine)}
        body={fullBody}
        actionLabel={actionLabel}
        onAction={onAction}
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
}
